/**
 * SA-formatted display helpers — currency, dates, expiry, byte sizes.
 *
 * Currency convention: `R 1 234,56` (space thousands, comma decimal).
 * Date convention: `dd MMM yyyy` (e.g. "29 Apr 2026") for human display.
 * Time zone: always `Africa/Johannesburg` in customer-facing strings.
 */

const ZAR_FORMATTER = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  currencyDisplay: 'narrowSymbol',
  minimumFractionDigits: 2,
});

const DATE_FORMATTER = new Intl.DateTimeFormat('en-ZA', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'Africa/Johannesburg',
});

const DATETIME_FORMATTER = new Intl.DateTimeFormat('en-ZA', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Africa/Johannesburg',
});

const ZAR_FORMATTER_WHOLE = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  currencyDisplay: 'narrowSymbol',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Format RANDS as ZAR currency — `formatZAR(1234.5) === 'R 1 234,50'`.
 *
 * ⚠️ THIS TAKES RANDS, NOT CENTS. Use `formatZARFromCents` for cents.
 *
 * The distinction is not pedantry. Before 2026-08-03 there were ~27 local
 * copies of a function called `formatZAR`, all with the identical signature
 * `(n: number) => string` — and it meant RANDS in ~22 files and CENTS in ~5
 * (ServiceOrderDetailPage, CareSubscriptionsPage, InstallmentRecoveryPage,
 * StudioStarterOrderPage, MyServiceOrderDetailPage). Copying a line between
 * pages — the most common way code moves in this repo — produced a figure
 * wrong by 100×, with no type error and no failing test. The unit now lives in
 * the NAME so that copy is impossible to make silently.
 */
export function formatZAR(amount: number): string {
  return ZAR_FORMATTER.format(amount).replace(/ /g, ' ');
}

/**
 * Format CENTS as ZAR currency — `formatZARFromCents(123450) === 'R 1 234,50'`.
 *
 * Use wherever the value came from a `*_cents` column (service_order,
 * installment_schedule, quote_metadata …). See the warning on `formatZAR`.
 */
export function formatZARFromCents(cents: number): string {
  return formatZAR(cents / 100);
}

/** Whole-rand variant for dense dashboard tiles — `R 2 192`, no decimals. */
export function formatZARWhole(amount: number): string {
  return ZAR_FORMATTER_WHOLE.format(amount).replace(/ /g, ' ');
}

/**
 * Format an ISO date string as `dd MMM yyyy`, in SAST.
 *
 * Null, undefined and unparseable inputs return `'—'` rather than throwing or
 * printing "Invalid Date". That behaviour is inherited from the ~20 local
 * copies this replaced (S2) — they all guarded the same way, so callers already
 * depend on it.
 *
 * Unlike those copies, this pins `Africa/Johannesburg`. They used the browser's
 * zone, which is the same thing for SA staff but silently wrong for anyone
 * travelling, and inconsistent with `public.sast_date()` server-side.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return DATE_FORMATTER.format(d);
}

/** Format an ISO datetime as `dd MMM yyyy HH:mm`, in SAST. See `formatDate`. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return DATETIME_FORMATTER.format(d);
}

/**
 * Coarse "how long ago" label — `just now` / `45s ago` / `12m ago` / `3h ago` /
 * `2d ago`. Consolidates the copies in AdminDashboard and SupportInboxPage.
 */
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const seconds = Math.floor((Date.now() - t) / 1000);
  if (seconds < 30) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** Human-friendly expiry phrase: "in 18 days" / "expired 4 days ago" / "today". */
export function relativeExpiry(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  const days = Math.round(ms / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  if (days > 0) return `in ${days} days`;
  return `${Math.abs(days)} days ago`;
}

/** Format MB into a human-readable size: 12 400 → "12.4 GB", 850 → "850 MB". */
export function formatMB(mb: number): string {
  if (mb >= 1_000_000) return `${(mb / 1_000_000).toFixed(1)} TB`;
  if (mb >= 1_000) return `${(mb / 1_000).toFixed(1)} GB`;
  return `${Math.round(mb)} MB`;
}

/** Format GB into a human-readable size for plan limits: 250 → "250 GB". */
export function formatGB(gb: number): string {
  if (gb >= 1_000) return `${(gb / 1_000).toFixed(1)} TB`;
  return `${gb} GB`;
}

/** "Unlimited" if -1, otherwise the integer formatted. */
export function formatLimit(n: number): string {
  return n === -1 ? 'Unlimited' : n.toLocaleString('en-ZA');
}

/** Compute a percent (0-100) of used vs total, capped at 100. */
export function usagePercent(used: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((used / total) * 100));
}

/** Tone for a usage bar: green < 70%, amber < 90%, red >= 90%. */
export function usageTone(percent: number): 'success' | 'warning' | 'danger' {
  if (percent >= 90) return 'danger';
  if (percent >= 70) return 'warning';
  return 'success';
}
